import { Badge, Button } from '@cloudflare/kumo';
import { useState, useEffect } from 'react';
import { useAppToast } from '../hooks/useAppToast';
import { ICONS, glyph, Glyph } from '../components/ui/icons';
import { palette } from '../lib/palette';
import { SkeletonText } from '../components/ui/Skeleton';

interface SystemStats {
  tenants: { total: number; active: number; inactive: number };
  users: { total: number; byRole: { SUPERADMIN: number; ADMIN: number; USER: number } };
  resources: { totalContacts: number; totalCampaigns: number; totalSessions: number; totalMessages: number };
  activity: { activeToday: number; activeCampaigns: number; workingSessions: number };
}

interface TenantStats {
  tenantId: string;
  tenantName: string;
  slug: string;
  contactsCount: number;
  campaignsCount: number;
  messagesCount: number;
  activeCampaigns: number;
  workingSessions: number;
  maxContacts: number;
  maxCampaigns: number;
  contactsUsagePct: number;
  campaignsUsagePct: number;
  quotaStatus: string;
  lastContactCreated?: string;
  lastCampaignCreated?: string;
}

interface RecentActivity {
  id: string;
  severity: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  resolved: boolean;
  description: string;
  timestamp: string;
  tenantName?: string;
}

const CARD: React.CSSProperties = {
  background: 'var(--surface)',
  border: '1px solid var(--border)',
  borderRadius: 6,
  overflow: 'hidden',
};

const SECTION_HDR: React.CSSProperties = {
  padding: '14px 20px',
  borderBottom: '1px solid var(--border)',
};

const TH: React.CSSProperties = {
  padding: '10px 16px',
  textAlign: 'left' as const,
  fontSize: 14,
  fontWeight: 600,
  color: 'var(--text)',
  background: 'var(--surface-2)',
};

const TD: React.CSSProperties = {
  padding: '12px 16px',
  borderTop: '1px solid var(--border)',
  fontSize: 13,
  color: 'var(--text)',
  verticalAlign: 'top',
};

function usagePctBarColor(pct: number) {
  if (pct >= 90) return 'var(--crm-text-error)';
  if (pct >= 70) return 'var(--crm-text-warning)';
  return 'var(--brand-text)';
}

function activityDotColor(activity: RecentActivity) {
  if (activity.resolved) return palette.green.dark1;
  switch (activity.severity) {
    case 'CRITICAL':
    case 'HIGH': return palette.red.base;
    case 'MEDIUM': return palette.yellow.dark2;
    default: return palette.blue.base;
  }
}

function activityGlyph(activity: RecentActivity) {
  if (activity.resolved) return 'CheckmarkWithCircle';
  return activity.severity === 'LOW' ? 'Bell' : 'Warning';
}

function formatTimeAgo(timestamp: string) {
  const diff = Date.now() - new Date(timestamp).getTime();
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  if (days > 0) return `${days}d atrás`;
  if (hours > 0) return `${hours}h atrás`;
  if (minutes > 0) return `${minutes}min atrás`;
  return 'Agora';
}

/** embedded: dentro da aba Visão geral da Plataforma (sem cabeçalho de página) */
export function SuperAdminDashboard({ embedded = false }: { embedded?: boolean } = {}) {
  const toast = useAppToast();
  const [stats, setStats] = useState<SystemStats | null>(null);
  const [tenantStats, setTenantStats] = useState<TenantStats[]>([]);
  const [recentActivity, setRecentActivity] = useState<RecentActivity[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
    const interval = setInterval(loadDashboardData, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadDashboardData = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const headers = { Authorization: `Bearer ${token}` };

      const [statsRes, tenantStatsRes] = await Promise.all([
        fetch('/api/system/stats', { headers }),
        fetch('/api/system/tenant-stats', { headers }),
      ]);

      if (statsRes.ok) setStats(await statsRes.json());
      if (tenantStatsRes.ok) setTenantStats(await tenantStatsRes.json());

      // Alertas reais (quedas de conexão, falhas de campanha, quotas...).
      // Antes este card exibia três eventos fictícios fixos no código.
      const alertsRes = await fetch('/api/alerts?limit=6', { headers });
      if (alertsRes.ok) {
        const { alerts = [] } = await alertsRes.json();
        setRecentActivity(alerts.map((a: any) => ({
          id: a.id,
          severity: a.severity,
          resolved: a.resolved,
          description: a.title,
          timestamp: a.createdAt,
          tenantName: a.tenant?.name,
        })));
      }
    } catch {
      toast.error('Erro ao carregar dados do dashboard');
    } finally {
      setLoading(false);
    }
  };

  const quotaBadgeColor = (status: string): 'red' | 'orange' | 'green' | 'neutral' => {
    if (status.includes('exceeded')) return 'red';
    if (status.includes('Near limit')) return 'orange';
    if (status.includes('OK')) return 'green';
    return 'neutral';
  };

  if (loading) {
    return (
      <>
        {!embedded && (
          <div style={{ display: 'flex', alignItems: 'flex-start', padding: '28px 40px 0', marginBottom: 24 }}>
            <div>
              <h2 className="text-xl font-semibold" style={{ marginBottom: 4 }}>Dashboard Sistema</h2>
              <SkeletonText lines={4} label="Carregando" />
            </div>
          </div>
        )}
        <div className={embedded ? undefined : 'page-content'}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 28 }}>
            {[1, 2, 3, 4].map(i => (
              <div key={i} style={{ ...CARD, padding: 24, height: 96, background: 'var(--surface-2)' }} />
            ))}
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {embedded ? (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
          <Button variant="secondary" icon={ICONS.Refresh} onClick={loadDashboardData}>
            Atualizar
          </Button>
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: '28px 40px 0', marginBottom: 24 }}>
          <div>
            <h2 className="text-xl font-semibold" style={{ marginBottom: 4 }}>Dashboard Sistema</h2>
            <div style={{ color: 'var(--text-muted)' }}>Visão geral do sistema multi-tenant</div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button variant="secondary" icon={ICONS.Refresh} onClick={loadDashboardData}>
              Atualizar
            </Button>
          </div>
        </div>
      )}

      <div className={embedded ? undefined : 'page-content'}>
        {/* ── Stat cards ── */}
        {stats && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 16, marginBottom: 28 }}>
            {[
              { glyph: 'Building', color: 'var(--link)', value: stats.tenants.total, label: 'Empresas', sub: `${stats.tenants.active} ativas` },
              { glyph: 'PersonGroup', color: '#7F59F0', value: stats.users.total, label: 'Usuários', sub: `${stats.users.byRole.ADMIN} admins` },
              { glyph: 'Person', color: 'var(--brand-text)', value: stats.resources.totalContacts.toLocaleString(), label: 'Contatos', sub: 'Total no sistema' },
              { glyph: 'Megaphone', color: '#F97216', value: stats.resources.totalCampaigns, label: 'Campanhas', sub: `${stats.activity.activeCampaigns} ativas` },
            ].map(({ glyph: icon, color, value, label, sub }) => (
              <div key={label} style={{ ...CARD, padding: '20px 20px', display: 'flex', alignItems: 'center', gap: 16 }}>
                <div style={{
                  width: 44, height: 44, borderRadius: 8, flexShrink: 0,
                  backgroundColor: `${color}18`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {glyph(icon, 32)}
                </div>
                <div>
                  <div style={{ fontSize: 26, fontWeight: 700, color: 'var(--text)', lineHeight: 1.1 }}>{value}</div>
                  <div className="!text-[var(--text-muted)] !text-sm !mt-0.5">{label}</div>
                  <div style={{ fontSize: 11, color, marginTop: 2 }}>{sub}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── Main grid: Tenant table + Activity ── */}
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 24, marginBottom: 24 }}>
          {/* Tenant Stats Table */}
          <div style={CARD}>
            <div style={SECTION_HDR}>
              <h4 className="text-base font-semibold">Status das Empresas</h4>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={TH}>Empresa</th>
                    <th style={TH}>Recursos</th>
                    <th style={TH}>Uso de quotas</th>
                    <th style={TH}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {tenantStats.length === 0 && (
                    <tr>
                      <td colSpan={4} style={{ ...TD, textAlign: 'center', color: 'var(--text-faint)', padding: 32 }}>
                        Nenhum dado disponível
                      </td>
                    </tr>
                  )}
                  {tenantStats.map((tenant) => (
                    <tr key={tenant.tenantId}>
                      <td style={TD}>
                        <div style={{ fontWeight: 600, color: 'var(--text)' }}>{tenant.tenantName}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-faint)', marginTop: 2 }}>{tenant.slug}</div>
                      </td>
                      <td style={TD}>
                        <div>{tenant.contactsCount} contatos</div>
                        <div style={{ color: 'var(--text-muted)' }}>{tenant.campaignsCount} campanhas interativas</div>
                        <div style={{ color: 'var(--text-muted)' }}>{tenant.messagesCount} mensagens</div>
                      </td>
                      <td style={TD}>
                        {[
                          { label: 'Contatos', pct: tenant.contactsUsagePct },
                          { label: 'Campanhas', pct: tenant.campaignsUsagePct },
                        ].map(({ label, pct }) => (
                          <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                            <span style={{ fontSize: 11, color: 'var(--text-muted)', width: 68 }}>{label}: {pct}%</span>
                            <div style={{ width: 64, height: 4, background: 'var(--border)', borderRadius: 2, overflow: 'hidden' }}>
                              <div style={{
                                width: `${Math.min(pct, 100)}%`,
                                height: '100%',
                                background: usagePctBarColor(pct),
                                borderRadius: 2,
                              }} />
                            </div>
                          </div>
                        ))}
                      </td>
                      <td style={TD}>
                        <Badge variant={quotaBadgeColor(tenant.quotaStatus)}>
                          {tenant.quotaStatus.replace(/[🔴🟡🟢]/gu, '').trim() || 'OK'}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Recent Activity */}
          <div style={CARD}>
            <div style={SECTION_HDR}>
              <h4 className="text-base font-semibold">Alertas recentes</h4>
            </div>
            <div style={{ padding: '12px 0' }}>
              {recentActivity.length === 0 && (
                <div style={{ textAlign: 'center', padding: '32px 16px' }}>
                  <ICONS.Bell size={24} />
                  <div className="!text-[var(--text-faint)] !mt-2">Nenhum alerta recente</div>
                </div>
              )}
              {recentActivity.map((activity) => {
                const color = activityDotColor(activity);
                return (
                  <div key={activity.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '10px 20px' }}>
                    <div style={{
                      width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
                      backgroundColor: `${color}15`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      {glyph(activityGlyph(activity), 16)}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="!text-[var(--text)] !text-sm">{activity.description}</div>
                      {activity.tenantName && (
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>Empresa: {activity.tenantName}</div>
                      )}
                      <div style={{ fontSize: 11, color: 'var(--text-faint)', marginTop: 2 }}>{formatTimeAgo(activity.timestamp)}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* ── System Health ── */}
        <div style={CARD}>
          <div style={SECTION_HDR}>
            <h4 className="text-base font-semibold">Saúde do Sistema</h4>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 0 }}>
            {[
              {
                glyph: ICONS.CheckmarkWithCircle, color: 'var(--brand-text)',
                title: 'Sistema Online', sub: 'Todos os serviços funcionando',
              },
              {
                glyph: ICONS.Database, color: 'var(--link)',
                title: 'Banco de Dados', sub: 'Conexão estável',
              },
              {
                glyph: ICONS.Mobile, color: '#7F59F0',
                title: 'WhatsApp',
                sub: stats ? `${stats.activity.workingSessions} sessões ativas` : 'Carregando...',
              },
            ].map(({ glyph, color, title, sub }, i) => (
              <div key={title} style={{
                padding: '24px 32px', textAlign: 'center',
                borderLeft: i > 0 ? '1px solid var(--border)' : undefined,
              }}>
                <div style={{
                  width: 56, height: 56, borderRadius: '50%', margin: '0 auto 12px',
                  backgroundColor: `${color}15`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Glyph icon={glyph} size={24} style={{ color }}/>
                </div>
                <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)' }}>{title}</div>
                <div className="!text-[var(--text-muted)] !text-sm !mt-1">{sub}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}
